import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { BarChart3, TrendingUp, CheckSquare, Calendar, Users, DollarSign } from "lucide-react";

import { API } from "@/config";

// Mock data for initial frontend development
const MOCK_DASHBOARD = {
  summary_stats: {
    total_opportunities: 24,
    total_revenue: 2450000,
    probable_revenue: 1875000,
    won_this_month: 3,
    lost_this_month: 1,
    average_close_probability: 65
  },
  top_priority_opportunities: [
    {
      opportunity: {
        id: "opp-001",
        customer_name: "Whole Foods Market",
        description: "National distribution deal for organic burritos",
        stage: "Negotiation",
        total_revenue: 450000,
        product_type: "Frozen Foods",
        business_type: "Retail",
        close_probability: 75,
        expected_close_date: "2025-02-15"
      },
      momentum_indicators: ["Recent meeting", "Decision maker engaged"],
      urgency_indicators: ["Contract expires soon"]
    },
    {
      opportunity: {
        id: "opp-002",
        customer_name: "Costco Wholesale",
        description: "Club pack expansion program",
        stage: "Proposal",
        total_revenue: 680000,
        product_type: "Frozen Foods",
        business_type: "Wholesale",
        close_probability: 60,
        expected_close_date: "2025-03-01"
      },
      momentum_indicators: ["Proposal reviewed"],
      urgency_indicators: []
    },
    {
      opportunity: {
        id: "opp-003",
        customer_name: "Target Corporation",
        description: "Regional test market program",
        stage: "Discovery",
        total_revenue: 225000,
        product_type: "Frozen Foods",
        business_type: "Retail",
        close_probability: 40,
        expected_close_date: "2025-04-01"
      },
      momentum_indicators: [],
      urgency_indicators: ["No activity in 14 days"]
    }
  ],
  open_action_items: [
    { id: "ai-001", title: "Send revised pricing to Whole Foods", due_date: "2025-01-13", priority: "high", opportunity_name: "Whole Foods Market" },
    { id: "ai-002", title: "Schedule demo with Costco buyer", due_date: "2025-01-15", priority: "medium", opportunity_name: "Costco Wholesale" },
    { id: "ai-003", title: "Follow up on Target sample request", due_date: "2025-01-14", priority: "high", opportunity_name: "Target Corporation" },
    { id: "ai-004", title: "Prepare Q1 forecast presentation", due_date: "2025-01-20", priority: "low", opportunity_name: null }
  ],
  recent_activities: [
    { id: "act-001", type: "meeting", description: "Call with Whole Foods procurement team", date: "2025-01-10", opportunity_name: "Whole Foods Market" },
    { id: "act-002", type: "email", description: "Sent proposal to Costco", date: "2025-01-09", opportunity_name: "Costco Wholesale" },
    { id: "act-003", type: "note", description: "Updated pricing model", date: "2025-01-08", opportunity_name: "Whole Foods Market" }
  ]
};

const MOCK_FUNNEL = {
  total_revenue: 2450000,
  total_probable_revenue: 1875000,
  funnel_stages: [
    { stage: "Lead", count: 8, total_revenue: 320000, probable_revenue: 48000 },
    { stage: "Discovery", count: 5, total_revenue: 425000, probable_revenue: 127500 },
    { stage: "Proposal", count: 4, total_revenue: 680000, probable_revenue: 340000 },
    { stage: "Negotiation", count: 3, total_revenue: 575000, probable_revenue: 431250 },
    { stage: "Closed - Won", count: 3, total_revenue: 350000, probable_revenue: 350000 },
    { stage: "Closed - Lost", count: 1, total_revenue: 100000, probable_revenue: 0 }
  ]
};

const CRMDashboard = ({ navigateToOpportunity, navigateToOpportunitiesWithFilter, navigateToActionItem }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [funnelData, setFunnelData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const [dashboardRes, funnelRes] = await Promise.all([
        axios.get(`${API}/crm/dashboard/summary`).catch(() => ({ data: MOCK_DASHBOARD })),
        axios.get(`${API}/crm/dashboard/sales-funnel`).catch(() => ({ data: MOCK_FUNNEL }))
      ]);
      setDashboardData(dashboardRes.data);
      setFunnelData(funnelRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard', error);
      // Use mock data as fallback
      setDashboardData(MOCK_DASHBOARD);
      setFunnelData(MOCK_FUNNEL);
    } finally {
      setLoading(false);
    }
  };

  const formatRevenue = (amount) => {
    if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(1)}mm`;
    } else if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(1)}k`;
    } else {
      return `$${amount.toFixed(0)}`;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStageColor = (stage) => {
    switch (stage) {
      case 'Lead': return 'bg-blue-100 text-blue-800';
      case 'Discovery': return 'bg-purple-100 text-purple-800';
      case 'Proposal': return 'bg-indigo-100 text-indigo-800';
      case 'Negotiation': return 'bg-orange-100 text-orange-800';
      case 'Closed - Won': return 'bg-green-100 text-green-800';
      case 'Closed - Lost': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-6">CRM Dashboard</h1>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      </div>
    );
  }

  const { summary_stats, top_priority_opportunities, open_action_items, recent_activities } = dashboardData || MOCK_DASHBOARD;
  const funnel = funnelData || MOCK_FUNNEL;

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6 flex items-center gap-2">
        <BarChart3 className="w-6 h-6" />
        CRM Dashboard
      </h1>

      {/* Summary Stats */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Opportunities</p>
                <p className="text-2xl font-bold">{summary_stats?.total_opportunities || 0}</p>
              </div>
              <Users className="w-8 h-8 text-indigo-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pipeline Value</p>
                <p className="text-2xl font-bold">{formatRevenue(summary_stats?.total_revenue || 0)}</p>
              </div>
              <DollarSign className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Probable Revenue</p>
                <p className="text-2xl font-bold">{formatRevenue(summary_stats?.probable_revenue || 0)}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Avg Close Probability</p>
                <p className="text-2xl font-bold">{summary_stats?.average_close_probability || 0}%</p>
              </div>
              <CheckSquare className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-2 gap-6 mb-6">
        {/* Sales Funnel */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              📊 Sales Funnel
            </CardTitle>
            <div className="text-sm text-gray-500">
              Total: <strong>{formatRevenue(funnel.total_revenue)}</strong> | Probable: <strong>{formatRevenue(funnel.total_probable_revenue)}</strong>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {funnel.funnel_stages.map((stage, index) => {
                const widthPercent = 100 - (index * 8);
                const isClosedLost = stage.stage === 'Closed - Lost';
                const isClosedWon = stage.stage === 'Closed - Won';
                
                return (
                  <div
                    key={stage.stage}
                    onClick={() => !isClosedLost && navigateToOpportunitiesWithFilter && navigateToOpportunitiesWithFilter(stage.stage)}
                    className={`mx-auto p-3 rounded-lg border-2 transition-all ${
                      isClosedLost 
                        ? 'bg-red-50 border-red-300 cursor-default' 
                        : isClosedWon 
                          ? 'bg-green-50 border-green-400 cursor-pointer hover:scale-[1.02] hover:shadow-md'
                          : 'bg-indigo-50 border-indigo-300 cursor-pointer hover:scale-[1.02] hover:shadow-md'
                    }`}
                    style={{ width: `${widthPercent}%` }}
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <div className="font-semibold text-sm">{stage.stage}</div>
                        <div className="text-xs text-gray-500">{stage.count} {stage.count === 1 ? 'opp' : 'opps'}</div>
                      </div>
                      <div className="text-right">
                        <div className={`font-bold ${isClosedLost ? 'text-red-600' : isClosedWon ? 'text-green-600' : 'text-gray-800'}`}>
                          {formatRevenue(stage.total_revenue)}
                        </div>
                        <div className="text-xs text-gray-500">{formatRevenue(stage.probable_revenue)} prob</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Top Priority Opportunities */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">🎯 Top Priority Opportunities</CardTitle>
            <p className="text-sm text-gray-500">AI-ranked by momentum, value, and urgency</p>
          </CardHeader>
          <CardContent className="max-h-[400px] overflow-y-auto">
            {top_priority_opportunities.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No active opportunities</div>
            ) : (
              <div className="space-y-3">
                {top_priority_opportunities.map((item, index) => {
                  const opp = item.opportunity;
                  return (
                    <div
                      key={opp.id}
                      onClick={() => navigateToOpportunity && navigateToOpportunity(opp.id)}
                      className={`p-3 rounded-lg border-l-4 cursor-pointer transition-all hover:-translate-y-0.5 hover:shadow-md ${
                        index === 0 ? 'bg-amber-50 border-amber-400' : 'bg-gray-50 border-indigo-400'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold">{opp.customer_name}</h4>
                          <Badge className={getStageColor(opp.stage)}>{opp.stage}</Badge>
                          {index === 0 && (
                            <Badge className="bg-amber-400 text-black">TOP</Badge>
                          )}
                        </div>
                        <div className="text-right">
                          <div className="font-bold">{formatRevenue(opp.total_revenue)}</div>
                          <div className="text-xs text-gray-500">Total Revenue</div>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{opp.description}</p>
                      <div className="flex gap-3 text-xs text-gray-500">
                        <span>📦 {opp.product_type}</span>
                        <span>🏢 {opp.business_type}</span>
                        <span>📊 {opp.close_probability}%</span>
                        {opp.expected_close_date && (
                          <span>📅 {new Date(opp.expected_close_date).toLocaleDateString()}</span>
                        )}
                      </div>
                      {item.momentum_indicators?.length > 0 && (
                        <div className="mt-2 text-xs text-green-600">
                          📈 {item.momentum_indicators.join(', ')}
                        </div>
                      )}
                      {item.urgency_indicators?.length > 0 && (
                        <div className="mt-1 text-xs text-red-600">
                          ⚠️ {item.urgency_indicators.join(', ')}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Action Items and Recent Activity */}
      <div className="grid grid-cols-2 gap-6">
        {/* Open Action Items */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <CheckSquare className="w-5 h-5" />
              Open Action Items
            </CardTitle>
          </CardHeader>
          <CardContent className="max-h-[300px] overflow-y-auto">
            {open_action_items.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No open action items</div>
            ) : (
              <div className="space-y-2">
                {open_action_items.map((item) => (
                  <div
                    key={item.id}
                    onClick={() => navigateToActionItem && navigateToActionItem(item.id)}
                    className="p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="font-medium text-sm">{item.title}</div>
                        {item.opportunity_name && (
                          <div className="text-xs text-gray-500 mt-1">🎯 {item.opportunity_name}</div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={getPriorityColor(item.priority)}>{item.priority}</Badge>
                        <span className="text-xs text-gray-500">
                          📅 {new Date(item.due_date).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent className="max-h-[300px] overflow-y-auto">
            {recent_activities.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No recent activity</div>
            ) : (
              <div className="space-y-2">
                {recent_activities.map((activity) => (
                  <div key={activity.id} className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="text-sm">{activity.description}</div>
                        {activity.opportunity_name && (
                          <div className="text-xs text-gray-500 mt-1">🎯 {activity.opportunity_name}</div>
                        )}
                      </div>
                      <span className="text-xs text-gray-500">
                        {new Date(activity.date).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CRMDashboard;
